import React, { useCallback, useEffect, useState } from 'react';
import { Alert, Box, Button, Chip, Stack, TextField, Typography } from '@mui/material';
import OfferPortalApi, { OfferMessageEdits, OfferPreview, OfferRecord } from '../../api/OfferPortalApi';

/**
 * Review and send (Lloyd, Aug 18: "which once complete, then sends it to the client").
 *
 * Recording and sending are two steps rather than one. The email quotes fees and carries Lloyd's
 * own recommendation to accept — a message of that weight gets looked at before it leaves, which is
 * the rule the offer drafts and the bond-to-court delivery already follow.
 *
 * The draft is built server-side from the saved record, then left editable (Lloyd, Sep 25: "It has
 * a pre-drafted letter, but no way to edit it"). What is in the boxes when Send is pressed is what
 * the client receives, plus the opt-out line every text ends with, so the screen still cannot drift
 * from the send.
 */

/** Twin of SMS_OPT_OUT_LINE and withOptOutLine in backend/src/utils/messages/smsOptOut.ts. */
const SMS_OPT_OUT_LINE = 'Reply STOP to opt out.';
function sentTextLength(text: string): number {
  const trimmed = text.trim();
  return trimmed.endsWith(SMS_OPT_OUT_LINE) ? trimmed.length : trimmed.length + 1 + SMS_OPT_OUT_LINE.length;
}
interface OfferSendPanelProps {
  offer: OfferRecord;
  onSent: (offer: OfferRecord) => void;
}

function OfferSendPanel({ offer, onSent }: OfferSendPanelProps) {
  const [preview, setPreview] = useState<OfferPreview | null>(null);
  const [edits, setEdits] = useState<OfferMessageEdits | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [sending, setSending] = useState(false);
  const [confirmingResend, setConfirmingResend] = useState(false);

  const alreadySent = Boolean(offer.conveyedAt);

  const load = useCallback(async () => {
    setError(null);
    try {
      const drafted = await OfferPortalApi.previewOffer(offer.guid);
      setPreview(drafted);
      setEdits({ subject: drafted.email.subject, body: drafted.email.body, smsText: drafted.smsText });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to build the preview');
    }
  }, [offer.guid]);

  useEffect(() => {
    void load();
  }, [load]);

  const send = async (resend: boolean) => {
    if (!edits) return;
    setSending(true);
    setError(null);
    setNotice(null);
    try {
      const result = await OfferPortalApi.sendOffer(offer.guid, edits, resend);
      const went = [result.emailSent ? 'email' : null, result.smsSent ? 'text' : null]
        .filter(Boolean)
        .join(' and ');
      // Partial success is reported as such: one channel landing is a real outcome, and the staff
      // member needs to know the other did not rather than reading "sent" and assuming both.
      setNotice(
        result.problems.length
          ? `Sent by ${went}. ${result.problems.join(', and ')}.`
          : `Sent to the client by ${went}.`,
      );
      setConfirmingResend(false);
      onSent(result.offer);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send the offer');
    } finally {
      setSending(false);
    }
  };

  if (!preview || !edits) {
    return error ? (
      <Alert severity="error" onClose={() => setError(null)}>
        {error}
      </Alert>
    ) : (
      <Typography variant="body2" color="text.secondary">
        Building the preview…
      </Typography>
    );
  }

  const edited =
    edits.subject !== preview.email.subject ||
    edits.body !== preview.email.body ||
    edits.smsText !== preview.smsText;
  const blank = !edits.subject.trim() || !edits.body.trim() || !edits.smsText.trim();
  const noRecipient = !preview.recipientEmail && !preview.recipientPhone;

  const restoreDraft = () =>
    setEdits({ subject: preview.email.subject, body: preview.email.body, smsText: preview.smsText });

  return (
    <Box>
      <Stack direction="row" spacing={2} alignItems="center" sx={{ mb: 1 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700 }}>
          Review and send to the client
        </Typography>
        {edited && (
          <Button size="small" onClick={restoreDraft}>
            Restore the drafted wording
          </Button>
        )}
      </Stack>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
        Edit the wording as needed. The client receives exactly what is in these boxes, and the text
        ends with "{SMS_OPT_OUT_LINE}".
      </Typography>

      <Stack direction="row" spacing={1} sx={{ mb: 2 }} flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={preview.recipientEmail ? 'default' : 'warning'}
          variant="outlined"
          label={preview.recipientEmail ? `Email: ${preview.recipientEmail}` : 'No email on file'}
        />
        <Chip
          size="small"
          color={preview.recipientPhone ? 'default' : 'warning'}
          variant="outlined"
          label={preview.recipientPhone ? `Text: ${preview.recipientPhone}` : 'No mobile number on file'}
        />
      </Stack>

      <TextField
        label="Email subject"
        value={edits.subject}
        onChange={(e) => setEdits({ ...edits, subject: e.target.value })}
        error={!edits.subject.trim()}
        fullWidth
        size="small"
        sx={{ mb: 1 }}
      />
      <TextField
        label="Email body"
        value={edits.body}
        onChange={(e) => setEdits({ ...edits, body: e.target.value })}
        error={!edits.body.trim()}
        fullWidth
        multiline
        minRows={10}
        sx={{ mb: 2 }}
      />
      <TextField
        label="Text message"
        value={edits.smsText}
        onChange={(e) => setEdits({ ...edits, smsText: e.target.value })}
        error={!edits.smsText.trim()}
        fullWidth
        size="small"
        multiline
        helperText={`${sentTextLength(edits.smsText)} characters as sent, with the opt-out line. Keep it short: over 160 is billed as more than one text.`}
        sx={{ mb: 2 }}
      />

      {notice && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setNotice(null)}>
          {notice}
        </Alert>
      )}
      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {alreadySent && !confirmingResend && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2" color="text.secondary">
            This offer has already been sent to the client.
          </Typography>
          <Button size="small" onClick={() => setConfirmingResend(true)}>
            Send it again
          </Button>
        </Stack>
      )}

      {alreadySent && confirmingResend && (
        <Stack direction="row" spacing={2} alignItems="center">
          <Typography variant="body2">Send this offer to the client a second time?</Typography>
          <Button variant="contained" color="warning" disabled={sending || blank} onClick={() => send(true)}>
            {sending ? 'Sending…' : 'Yes, send again'}
          </Button>
          <Button size="small" onClick={() => setConfirmingResend(false)}>
            Cancel
          </Button>
        </Stack>
      )}

      {!alreadySent && (
        <Button
          variant="contained"
          disabled={sending || blank || noRecipient}
          onClick={() => send(false)}
        >
          {sending ? 'Sending…' : 'Send to client'}
        </Button>
      )}

      {blank && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          The subject, email and text message cannot be empty.
        </Typography>
      )}

      {noRecipient && (
        <Typography variant="caption" color="error" sx={{ display: 'block', mt: 1 }}>
          This client has neither an email address nor a mobile number on file, so there is nowhere
          to send the offer. Add one to their record first.
        </Typography>
      )}
    </Box>
  );
}

export default OfferSendPanel;
